import { elements } from '@moleculeone/draw';
import { Icon, styled } from '@moleculeone/ui';
import { Mol } from '@retro-ui/api-contract';
import { useMemo, useState } from 'react';

import { createElementsTable } from './utils/createElementsTable';

const elementsTable = createElementsTable(elements);

const elementButtonColors = (color: string) => ({
  'backgroundColor': `$${color}4`,
  'color': `$slate12`,
  '&:hover, &:focus': {
    backgroundColor: `$${color}6`,
  },
});
const ElementButton = styled('button', {
  'border': 'none',
  'width': '2rem',
  'padding': '$2',
  'borderRadius': '2px',
  'cursor': 'pointer',
  'transition': 'background-color .15s',
  '&[disabled]': {
    backgroundColor: '$slate2',
    borderColor: '$slate5',
    color: '$slate8',
    pointerEvents: 'none',
  },
  'variants': {
    type: {
      nonmetal: elementButtonColors('purple'),
      metal: elementButtonColors('green'),
      metalloid: elementButtonColors('amber'),
      alkali: elementButtonColors('indigo'),
      noble: elementButtonColors('tomato'),
      hydrogen: elementButtonColors('purple'),
      halogen: elementButtonColors('indigo'),
    },
  },
});

const ElementSearch = styled('form', {
  position: 'relative',
});
const ElementInput = styled('input', {
  'border': 'none',
  'outline': 'none',
  'backgroundColor': '$slate3',
  'marginBottom': '$4',
  'padding': '$2 $7 $2 $8',
  'width': '100%',
  'borderRadius': '0.5rem',
  '&:focus': {
    backgroundColor: '$slate4',
  },
});
const ElementInputIcon = styled(Icon, {
  pointerEvents: 'none',
  position: 'absolute',
  top: '$2',
  left: '$3',
});

type PeriodicTableProps = {
  onPickAtom: (mol: Mol) => void;
};

export const PeriodicTable = (props: PeriodicTableProps) => {
  const [searchString, setSearchString] = useState('');

  const matchingElements = useMemo(() => {
    if (!searchString) {
      return [];
    }
    const searchLowerCase = searchString.toLowerCase();
    const matching: string[] = [];
    elementsTable.forEach(row => {
      row.forEach(cell => {
        if (cell === null) {
          return;
        }
        const { element = '', name = '' } = cell;
        const matches = element.toLowerCase().includes(searchLowerCase) || name.toLowerCase().includes(searchLowerCase);
        if (matches) {
          matching.push(element);
        }
      });
    });
    return matching;
  }, [searchString]);

  const createOnClickHandler = element => () => {
    setSearchString('');
    props.onPickAtom({
      atoms: {
        0: { idx: '0', charge: 0, element },
      },
      bonds: {},
      nextAtom: 1,
      nextBond: 0,
      positions: {
        0: [0, 0, 0],
      },
    });
  };

  const onSearchChange = event => {
    setSearchString(event.target.value);
  };

  // when only one element left after search, pressing 'enter' fires click callback on this element
  const onSubmit = event => {
    event.preventDefault();
    if (matchingElements.length === 1) {
      const onClick = createOnClickHandler(matchingElements[0]);
      onClick();
    }
  };

  return (
    <>
      <ElementSearch onSubmit={onSubmit}>
        <ElementInput
          type="text"
          autoFocus
          name="elementSearch"
          placeholder="Search by name or symbol"
          onChange={onSearchChange}
        />
        <ElementInputIcon icon="search" size="small" />
      </ElementSearch>
      <table>
        <tbody>
          {elementsTable.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => {
                if (cell === null) {
                  return <td key={cellIndex} />;
                }
                const { element, type } = cell;
                const matched = searchString ? matchingElements.includes(element) : true;
                return (
                  <td key={element}>
                    <ElementButton type={type} disabled={!matched} onClick={createOnClickHandler(element)}>
                      {element}
                    </ElementButton>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
